"use client"

import React from "react";

import Image from "next/image";
import Countdown from "@/components/global/countdown/Countdown";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTAButton} from "@/shadcn/ui/button";
import {motion} from "framer-motion";

import {PageIQCapitalLaunchSchema} from "@/sanity/pages/PageIQCapitalLaunchTemplate";

import lastBg from "@/public/iq-capital-launch/lastBg.png";
import dotsLeft from "@/public/iq-capital-launch/dots-left.svg";
import dotsRight from "@/public/iq-capital-launch/dots-right.svg";
import noise from "@/public/iq-capital-launch/noise.png";

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeInDelay = (delay: number) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay }
});

export default function LastSection({
  data,
  className
}: {
  data: PageIQCapitalLaunchSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative pb-[8rem] lg:pb-[12rem]",
      className
    )}>
      <div className="container flex flex-col items-center">
        <div className="w-[min(176.5rem,100%)] relative overflow-clip rounded-[3rem] lg:rounded-[4rem] pt-[6.5rem] lg:pt-[10rem] pb-[35rem] lg:pb-[32.5rem] px-[2rem] flex flex-col items-center bg-gradient-to-b from-[#2B715A] to-[#0C1915]">
          <Image
            className="absolute !w-[126.5rem] !max-w-[unset] bottom-0 lg:-bottom-[5rem] left-1/2 -translate-x-[calc(50%+3rem)] pointer-events-none"
            src={lastBg}
            alt=""
          />
          <Image
            className="absolute !h-full !w-auto left-0 top-0 pointer-events-none"
            src={dotsLeft}
            alt=""
          />
          <Image
            className="absolute !h-full !w-auto right-0 top-0 pointer-events-none hidden lg:block"
            src={dotsRight}
            alt=""
          />
          <Image
            className="absolute !w-full !h-full object-cover left-0 top-0 pointer-events-none"
            src={noise}
            alt=""
          />

          <motion.div
            {...fadeInDelay}
            className="bg-[#F5F6F5] border border-[#324003]/10 rounded-full py-[.5rem] pl-[.5rem] pr-[1.5rem] mb-[1.5rem] relative"
          >
            {data?.lastChip && (
              <Image
                className="!w-[16.375rem]"
                src={data.lastChip}
                alt=""
              />
            )}
          </motion.div>

          {data?.lastTitle && (
            <motion.div
              {...fadeInDelay(0.1)}
              className="richtext w-[min(92.75rem,100%)] prose-p:text-[2.25rem] prose-p:w-[min(69.5rem,100%)] relative mb-[2rem] lg:mb-[4rem]"
            >
              <PortableText
                value={data.lastTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          <motion.div
            {...fadeInDelay(0.2)}
            className="w-[min(69.5rem,100%)] backdrop-blur border border-white/10 bg-white/5 rounded-[1rem] pt-[3rem] lg:pt-[4rem] pb-[2rem] lg:pb-[3rem] relative"
          >
            <Countdown
              className="relative flex-col-reverse gap-[4rem] lg:[&_.countdownContainer]:gap-[3rem]"
              dateTimeString={data?.countdown ?? ""}
            />
          </motion.div>

          {data?.lastCTA && (
            <motion.div {...fadeInDelay(0.3)}>
              <CTAButton
                className="mt-[2rem] lg:mt-[3rem] relative"
                data={data.lastCTA}
              />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
